import { useEffect, useMemo, useState } from "react";
import Accordion from "../../../common/accordion";
import { useAppSelector } from "../../../../store/hooks/StoreHooks";
import useCollapseFacade from "../../../../facade/collapse.Facade";
import Dropdown from "../../../common/dropdown";
import EquipmentTableAddChargePump from "../../../common/equipment-table-charge-pump";
import Checkbox from "../../../common/checkbox";
import ButtonGroup from "../../../common/button-group";
import LABELS from "../../../../constants/labels/cables.labels";
import useCableFacade from "../../../../facade/calculation/calculation-output/cable.facade";
import { cableEquipmentColumns, cableMotorLockEquipmentColumns } from "../../../../constants/columns/calculations/cableEquipmentTable";
import useTubingFacade from "../../../../facade/calculation/calculation-input/tubing.facade";
import ScatterLineChart from "./ScatterLineChart";
import useCalculationFacade from "../../../../facade/calculation/calculation.facade";
import Select from "../../../common/select";

const CableClearance = () => {

  const [wellModifiedList, setWellModifiedList] = useState<{ id: string, name: string }[]>([])
  const [plateModifiedList, setPlateModifiedList] = useState<{ id: string, name: string }[]>([])
  const [motorModifiedList, setMotorModifiedList] = useState<{ id: string, name: string }[]>([])
  const [motorEquipmentList, setMotorEquipmentList] = useState<any[]>([{}])
  const [lockPlateEquipmentList, setLockPlateEquipmentList] = useState<any[]>([{}])
  const { selectedLowerEquipment } = useAppSelector((state) => state.protector);
  const { schematicData, intakeMD } = useAppSelector((state) => state.calculations);
  const { cableClearingCollapse } = useAppSelector((state) => state?.collapse);
  const { tab1Input } = useAppSelector((state) => state?.tubing);
  const {
    wellCableList,
    lockPlateList,
    motorLeadList,
    ShowEquipmentTable,
    ShowEquipmentCableTable,
    wellEquipmentList,
    selectedWellEquipment,
    motorLockEquipmentList,
    selectedWellLockEquipment,
    cableClearance,
    tubingCableChart,
    pumpMotorLeadChart,
    sealMotorLeadChart,
    motorChart
  } = useAppSelector((state) => state?.cable)

  const { updateCableClearingCollapse } = useCollapseFacade();
  const { updateSchematicEquipmentData } = useCalculationFacade();
  const {
    updateHiddenCableDriftId,
    updateHiddenCablePumpLockPlate,
    updateHiddenCableSealLockPlate,
    updateHiddenCableMotorLockPlate
  } = useTubingFacade();
  const {
    updateWellChange,
    updateSelectedMotorEquipment,
    updateSelectedLockPlate,
    fetchMotorEquipment,
    fetchWellEquipment,
    updateSelectedWellEquipment,
    updateSelectedLockPlateEquipment,
    refreshChart,
    getSelectedCable,
  } = useCableFacade();

  const chart1Config = {
    seriesDefaults: {
      type: "scatterLine" as const,
      style: "smooth" as const,
      markers: {
        visible: false,
      },
      width: 1.0,
    },
    legend: {
      position: "bottom",
      labels: {
        font: "8px Segoe UI",
        color: "#fff",
      },
    },
    series: [
      {
        name: "Casing",
        data: tubingCableChart?.[0],
        color: "white",
      },
      {
        name: "Tubing",
        data: tubingCableChart?.[1],
        color: "blue",
      },
      {
        name: "Cable",
        data: tubingCableChart?.[2],
        color: "red",
      },
    ],
  };


  const chart2Config = {
    seriesDefaults: {
      type: "scatterLine" as const,
      style: "smooth" as const,
      markers: {
        visible: false,
      },
      width: 1.0,
    },
    legend: {
      position: "bottom",
      labels: {
        font: "8px Segoe UI",
        color: "#fff",
      },
    },
    series: [
      {
        name: "Casing",
        data: pumpMotorLeadChart?.[0],
        color: "white",
      },
      {
        name: "Pump",
        data: pumpMotorLeadChart?.[3],
        color: "blue",
      },
      {
        name: "MotorLead",
        data: pumpMotorLeadChart?.[4],
        color: "red",
      },
    ],
  };

  const chart3Config = {
    seriesDefaults: {
      type: "scatterLine" as const,
      style: "smooth" as const,
      markers: {
        visible: false,
      },
      width: 1.0,
    },
    legend: {
      position: "bottom",
      labels: {
        font: "8px Segoe UI",
        color: "#fff",
      },
    },
    series: [
      {
        name: "Casing",
        data: sealMotorLeadChart?.[0],
        color: "white",
      },
      {
        name: "Protector",
        data: sealMotorLeadChart?.[5],
        color: "blue",
      },
      {
        name: "MotorLead",
        data: sealMotorLeadChart?.[6],
        color: "red",
      },
    ],
  };

  const chart4Config = {
    seriesDefaults: {
      type: "scatterLine" as const,
      style: "smooth" as const,
      markers: {
        visible: false,
      },
      width: 1.0,
    },
    legend: {
      position: "bottom",
      labels: {
        font: "9px Segoe UI",
        color: "#fff",
      },
    },
    series: [
      {
        name: "Casing",
        data: motorChart?.[0],
        color: "white",
      },
      {
        name: "Motor",
        data: motorChart?.[7],
        color: "blue",
      },
    ],
    count: 2,
  };

  useEffect(() => {
    if (wellEquipmentList?.length > 0) {
      const wellEquipment = wellEquipmentList.map((equipment: any) => ({
        ...equipment,
        value: equipment?.id,
        label: equipment?.pN_Description,
      }));
      setMotorEquipmentList(wellEquipment);
    }
    if (motorLockEquipmentList?.length > 0) {
      const motorEquipment = motorLockEquipmentList.map((equipment: any) => ({
        ...equipment,
        value: equipment?.id,
        label: equipment?.mle,
      }));
      setLockPlateEquipmentList(motorEquipment);
    }
  }, [wellEquipmentList, motorLockEquipmentList])

  useEffect(() => {
    let plateValue = Object.entries(lockPlateList)?.map((records: any) => {
      return {
        id: records[0],
        name: records[0]
      }
    })
    let motorValue = Object.entries(motorLeadList)?.map((records: any) => {
      return {
        id: records[1],
        name: records[0]
      }
    })
    // setWellModifiedList(wellValue);
    setPlateModifiedList(plateValue);
    setMotorModifiedList(motorValue);
  }, [wellCableList, motorLeadList]);

  
  const handleCollapseChange = (key: boolean) => {
    updateCableClearingCollapse(key);
  };

  const handleWellChange = (option: any) => {
    updateWellChange({ show: false, selected: option?.id });
    getSelectedCable(option?.label);
    if (option?.id !== tab1Input?.hdnCable_CableType) {
      onClearWellEquipment();
    }
  };

  const handleMotorChange = (option: any) => {
    updateSelectedMotorEquipment(option?.id)
  }

  const handleLockPlateChange = (option: any) => {
    updateSelectedLockPlate(option?.id)
    fetchMotorEquipment();
  }

  const onClearWellEquipment = () => {
    updateSelectedWellEquipment([{}]);
    updateSchematicEquipmentData();
  };

  const onClearMotorEquipment = () => {
    updateSelectedLockPlateEquipment([{}]);
    updateSchematicEquipmentData();
  };

  const handleRefreshChart = () => {
    refreshChart()
  };

  const handleDriftId = (e: any) => {
    updateHiddenCableDriftId(e.target.checked);
  };

  const handlePumplockPlate = (e: any) => {
    updateHiddenCablePumpLockPlate(e.target.checked);
  };
  const handlemotorPlate = (e: any) => {
    updateHiddenCableMotorLockPlate(e.target.checked);
  };
  const handlesealLockPlate = (e: any) => {
    updateHiddenCableSealLockPlate(e.target.checked);
  };
  const handleEquipmentChange = (rows: any, rowIndex: number) => {
    let value = [rows];
    let cableLength = selectedLowerEquipment?.length > 0 ? selectedLowerEquipment?.[0]?.setDepth : intakeMD;
    const updatedCableEquipments = value?.map((equipment: any) => ({
      ...equipment,
      length: cableLength,
      totalLength: cableLength,
      dimensions: equipment.dimensions,
      nominalWeight: parseFloat(((cableLength * parseFloat((equipment?.nominalWeight || 0).toFixed(2))).toFixed(2)))
    }));
    value = [...updatedCableEquipments];

    updateSelectedWellEquipment(value);
    updateSchematicEquipmentData();
  }

  return (
    <Accordion
      tooltipText="Formulas"
      link={'/help?tab=Cable#CableCleavolt'}
      title="Cable clearance"
      isOpen={cableClearingCollapse}
      onToggle={() => handleCollapseChange(!cableClearingCollapse)}
      content={
        <>
          <div className="flex justify-center items-start p-4 pb-0 gap-4 w-1/2">
            <div className="flex flex-col justify-between items-center w-full align-top">
              <div className="flex justify-between items-center w-full space-x-4">
                <Select
                  label={"Well cable"}
                  options={wellCableList}
                  onChange={(option) => handleWellChange(option)}
                  padding="px-[14px] py-[5px]"
                  fontSize="16px"
                  className="sm:px-0 px-[10px] w-[224px]"
                  selectedValue={tab1Input?.hdnCable_CableType || 0}
                  name={"chargePump"}
                  width="50rem"
                />
              </div>
            </div>
          </div>
          {
            (ShowEquipmentTable || selectedWellEquipment?.length > 0) &&
            <div className="flex justify-center items-start p-4 pb-0 gap-4 w-full">
              <EquipmentTableAddChargePump
                options={motorEquipmentList || []}
                equipments={selectedWellEquipment || [{}]}
                onClear={onClearWellEquipment}
                onChange={(rows: any, rowIndex: number) => handleEquipmentChange(rows, rowIndex)}
                rowLength={1}
                equipmentGridParentContainer="SRCablesWell"
                columns={cableEquipmentColumns || []}
              />
            </div>
          }
          <div className="flex justify-center items-start p-4 pb-0 gap-4 w-full">
            <div className="flex flex-col justify-between items-center w-full align-top">
              <div className="flex justify-between items-center w-full space-x-4">
                <Dropdown
                  label={"Motor lead"}
                  options={motorModifiedList}
                  onSelect={(option) => handleMotorChange(option)}
                  dropdownBgColor="bg-darkcolor-800"
                  bgColor="bg-transparent"
                  selected={tab1Input?.hdnCable_MotorLead || motorModifiedList?.[0]?.id}
                  width="1/3%"
                />
                <Dropdown
                  label={"Lock plate"}
                  options={plateModifiedList}
                  onSelect={(option) => handleLockPlateChange(option)}
                  dropdownBgColor="bg-darkcolor-800"
                  bgColor="bg-transparent"
                  selected={tab1Input?.hdnCable_LockPlate || plateModifiedList?.[0]?.id}
                  width="1/3"
                />
              </div>
            </div>
          </div>
          {
            ShowEquipmentCableTable &&
            <div className="flex justify-center items-start p-4 gap-4 w-full">
              <EquipmentTableAddChargePump
                options={lockPlateEquipmentList || []}
                equipments={selectedWellLockEquipment}
                onClear={onClearMotorEquipment}
                rowLength={1}
                onChange={(rows: any, rowIndex: number) => updateSchematicEquipmentData()}
                equipmentGridParentContainer="SRMLE"
                columns={cableMotorLockEquipmentColumns || []}
              />
            </div>
          }
          <div className="flex items-center space-x-4 ml-4">
            <Checkbox
              label="Drift ID"
              checked={tab1Input?.hdnCable_IsDriftID || false}
              name="driftID"
              onChange={(e) => handleDriftId(e)}
            />
            {/* <ButtonGroup
              buttonOptions={[
                { id: 1, icon: "", label: `${LABELS?.click}`, type: "" },
              ]}
              handleClick={() => { }}
            /> */}
            <ButtonGroup
              buttonOptions={[
                { id: 1, icon: "", label: `${LABELS?.refreshCHart}`, type: "" },
              ]}
              handleClick={() => { handleRefreshChart() }}
            />
          </div>

          <div className="grid grid-cols-4 gap-4 p-4 text-white">
            <div className="bg-[#1E293B] p-4 rounded-lg border border-darkcolor-400">
              <h3 className="text-md font-bold mb-2 border-b  border-white pb-2">
                Casing-Tubing-Cable
              </h3>
              <ScatterLineChart {...chart1Config} />
              <p className="text-sm text-gray-400 mt-2">
                Clearance: <span className="text-white">{cableClearance?.ClrnceForTubng} {tab1Input?.actNameCable_ClearanceTubing}</span>
              </p>
              <p className="text-sm mt-2">
                {cableClearance?.MsgsForTubng}
              </p>
            </div>
            <div className="bg-[#1E293B] p-4 rounded-lg border border-darkcolor-400">
              <h3 className="text-md font-bold mb-2 border-b  border-white pb-2">
                Casing-Pump-Motor lead
              </h3>
              <ScatterLineChart {...chart2Config} />
              <Checkbox
                label="Pump lock plate"
                checked={tab1Input?.hdnCable_PumpLockPlate || false}
                name="motorPlate"
                onChange={(e) => handlePumplockPlate(e)}
              />
              <p className="text-sm text-gray-400 mt-2">
                Clearance: <span className="text-white">{cableClearance?.ClrnceForPmp} {tab1Input?.actNameCable_ClearancePump}</span>
              </p>
              <p className="text-sm mt-2"> {cableClearance?.MsgsForPmp}</p>
            </div>
            <div className="bg-[#1E293B] p-4 rounded-lg border border-darkcolor-400">
              <h3 className="text-md font-bold mb-2 border-b  border-white pb-2">
                Casing-Seal-Motor lead
              </h3>
              <ScatterLineChart {...chart3Config} />
              <Checkbox
                label="Seal lock plate"
                checked={tab1Input?.hdnCable_SealLockPlate || false}
                name="lockPlate"
                onChange={(e) => handlesealLockPlate(e)}
              />
              <p className="text-sm text-gray-400 mt-2">
                Clearance: <span className="text-white">{cableClearance?.ClrnceForPrtctr} {tab1Input?.actNameCable_ClearanceProtector}</span>
              </p>
              <p className="text-sm mt-2">
                {cableClearance?.MsgsForProtctr}
              </p>
            </div>
            <div className="bg-[#1E293B] p-4 rounded-lg border border-darkcolor-400">
              <h3 className="text-md font-bold mb-2 border-b  border-white pb-2">
                Casing-Motor
              </h3>
              <ScatterLineChart {...chart4Config} />
              <Checkbox
                label="Motor lock plate"
                checked={tab1Input?.hdnCable_MotorLockPlate || false}
                name="sealLockPlate"
                onChange={(e) => handlemotorPlate(e)}
              />
              <p className="text-sm text-gray-400 mt-2">
                Clearance: <span className="text-white">{cableClearance?.ClrnceForMotr} {tab1Input?.actNameCable_ClearanceMotor}</span>
              </p>
              <p className="text-sm mt-2">
                {cableClearance?.MsgsForMotr}
              </p>
            </div>
          </div>
        </>
      }
    />
  );
};

export default CableClearance;
